import {
  useEffect,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useRouter } from 'next/router';
import { useAuth0 } from '@auth0/auth0-react';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { getQuestionModule } from '../actions/questions';

const useQuestionModule = (moduleName) => {
  const dispatch = useDispatch();
  const router = useRouter();
  const user = useAuth0();
  const authStore = useSelector((state) => state?.authStore || {}, shallowEqual);
  const authReady = !user.isLoading && (
    (user.isAuthenticated && authStore.id_token)
    || !user.isAuthenticated
  );

  const [triggerRedirect, setTriggerRedirect] = useState(false);

  const questionModule = useSelector((state) => (
    state?.questions?.modules?.[moduleName]
  ), shallowEqual);

  useEffect(() => {
    if (authReady) {
      dispatch(getQuestionModule(moduleName));
    }
  }, [moduleName, dispatch, authReady]);

  const requireCompletion = useCallback((returnTo) => {
    if (
      questionModule
      && questionModule.isFetching === false
      && !questionModule.isComplete
      && moduleName !== '[moduleName]' // safeguard for random dev bug
    ) {
      setTriggerRedirect(returnTo || true);
      return true;
    }
    return false;
  }, [moduleName, questionModule]);

  // wait for auth to complete before redirecting
  useEffect(() => {
    if (authReady && triggerRedirect) {
      setTriggerRedirect(false);
      router.push({
        pathname: `/questions/module/${moduleName}`,
        query: {
          returnTo: encodeURIComponent(typeof triggerRedirect === 'string' ? triggerRedirect : router.asPath),
        },
      });
    }
  }, [authReady, moduleName, router, triggerRedirect]);

  const moduleData = useMemo(() => (
    authReady && questionModule && !questionModule.isFetching
      ? { ...questionModule, requireCompletion }
      : null
  ), [authReady, questionModule, requireCompletion]);

  return moduleData;
};

export default useQuestionModule;
